import { CarInsuranceQuote } from '@hyphengroup/cms-components/components/ProductCard/SectionPremiumCI';
import { DriverInput, VehicleInput } from '__generated__/graphql';
import { ClientProductsDomainService } from 'car-insurance/api/client/clientDomainServices';
import { CAR_INSURANCE_POLLING_WINDOW_MS } from 'car-insurance/constants';
import { Driver, Prices, Vehicle } from 'car-insurance/context';
import { fetchCarInsuranceDataMMProps } from './types';
import { CmsServiceClientVehiclesDomainService } from '$/app/(cms)/_modules/cmsService/carInsurance/cmsServiceClientVehiclesDomainService';
import { PremiumListResponse } from '$/components/cms/components-internal/CarInsurance/interface';

export const fetchCarInsuranceBolttechDataWithPolling = async (
  params: fetchCarInsuranceBolttechDataProps,
): Promise<PremiumListResponse> => {
  const {
    vehicle: vehicleSource,
    ncd,
    offenceFree,
    offPeak,
    noOfClaims,
    driver,
  } = params;
  const domainService = new ClientProductsDomainService();

  const vehicle: VehicleInput = {
    manufactureYear: Number(vehicleSource.manufactureYear),
    brandName: vehicleSource.makeName || '',
    modelCode: vehicleSource.modelCode,
    variantCode: vehicleSource.trim || '',
    // ToDo: check requirement for these fields
    engineCapacity: undefined,
    seatingCapacity: undefined,
    vehicleAge: undefined,
  };
  const timeout = CAR_INSURANCE_POLLING_WINDOW_MS;
  const refetchInterval = 1500;
  const startTime = Date.now();

  if (!driver) return { completed: 'fail', premiums: [] };

  // eslint-disable-next-line unused-imports/no-unused-vars
  const mainDriver: DriverInput = {
    age: driver.age,
    dateOfBirth: driver.dateOfBirth,
    gender: driver.gender,
    maritalStatus: driver.maritalStatus,
    occupation: driver.occupationType,
    occupationType: driver.occupationType,
    totalDrivingExperience: driver.totalDrivingExperience,
  };

  const requestId = await domainService.getCompareQuote(
    { mainDriver, vehicle, ncd, noOfClaims, offenceFree, offPeak },
    true,
  );

  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const premiums = await domainService.getPremiums({
          requestId,
          useBolttechData: true,
        });

        if (
          typeof premiums === 'object' &&
          premiums !== null &&
          'completed' in premiums
        ) {
          if (premiums.completed || Date.now() - startTime >= timeout) {
            resolve(premiums);
          } else {
            setTimeout(poll, refetchInterval);
          }
        } else if (Date.now() - startTime < timeout) {
          // A malformed poll response (e.g. GraphQL error with null data)
          // must not strand the promise — keep polling out the window
          setTimeout(poll, refetchInterval);
        } else {
          resolve({ completed: 'fail', premiums: [] });
        }
      } catch (error) {
        reject(error);
      }
    };

    poll();
  });
};

export const fetchCarInsuranceDataMM = async (
  props: fetchCarInsuranceDataMMProps,
): Promise<CarInsuranceQuote[]> => {
  const { driver, prices, vehicle } = props;
  const cms = new CmsServiceClientVehiclesDomainService();

  if (!driver) {
    return [];
  }

  const premiums = await cms.getCarInsurancePremiums(
    vehicle.makeName || '',
    vehicle.modelCode,
    +vehicle.manufactureYear,
    vehicle.trim || '',
    prices.propertyDamage ?? 0,
    prices.personalAccident ?? 0,
    prices.isActsOfNature || false,
    vehicle.type,
  );

  const quotes = premiums?.getCarInsuranceQuoteRequest?.quotes;
  if (!quotes) {
    return [];
  }

  return quotes.filter(Boolean).map(
    (q) =>
      ({
        deductible: q!.deductible,
        fmv: q!.fmv,
        netPremium: q!.netPremium,
        prices: {
          personalAccident: q!.prices!.personalAccident,
          bodilyInjury: q!.prices!.bodilyInjury,
          propertyDamage: q!.prices!.propertyDamage,
          ownDamageTheft: q!.prices!.ownDamageTheft,
          actOfNature: q!.prices!.actOfNature,
        },
        taxes: {
          documentStamps: q!.taxes!.documentStamps,
          localGovernment: q!.taxes!.localGovernment,
          vat: q!.taxes!.vat,
        },
        productId: q!.productId,
        productName: q!.productName,
        providerId: q!.providerId,
        providerName: q!.providerName,
        totalPremium: q!.totalPremium,
      }) as CarInsuranceQuote,
  );
};

export type fetchCarInsuranceBolttechDataProps = {
  vehicle: Vehicle;
  ncd: number;
  offenceFree: string;
  offPeak: string;
  noOfClaims: number;
  driver?: Driver;
  useBolttechData?: boolean;
  prices?: Prices;
  usage?: string;
};
